import { KeyboardEvent, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'
import useIsReadOnly from '../../hooks/useIsReadOnly'
import {
    RoutineDraft,
    useCreateRoutine,
    useCreateRoutineItem,
    useDuplicateRoutine,
    useModifyRoutine,
    useModifyRoutineItem,
} from '../../services/api/routines.hooks'
import { Colors, Spacing, Typography } from '../../styles'
import { icons } from '../../styles/images'
import { TRoutine } from '../../utils/types'
import GTInput from '../atoms/GTInput'
import GTModal from '../atoms/GTModal'
import GTButton from '../atoms/buttons/GTButton'
import { BodySmall } from '../atoms/typography/Typography'

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing._8};
    margin-bottom: ${Spacing._16};
`

const Label = styled(BodySmall)`
    color: ${Colors.text.muted};
    ${Typography.bold};
`

const ItemRow = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto auto;
    align-items: center;
    gap: ${Spacing._4};
`

const AddRow = styled.div`
    display: flex;
    align-items: center;
    gap: ${Spacing._8};
`

interface RoutineEditModalProps {
    routine?: TRoutine
    isCreating: boolean
}

// Routine order is set only by moving the routine's card on Daily Overview.
const RoutineEditModal = ({ routine, isCreating }: RoutineEditModalProps) => {
    const navigate = useNavigate()
    const isReadOnly = useIsReadOnly()
    // During a frontend-first rollout, an older server can resolve the routine without returning
    // its nested ids. The refetch fills those in; until then only the fake step ids stay inert.
    const areExistingItemsPending = Boolean(routine?.optimisticId && routine.id !== routine.optimisticId)
    const close = () => navigate('/routines')
    const [name, setName] = useState(routine?.name ?? '')
    const [draftItems, setDraftItems] = useState<string[]>([])
    const [newItem, setNewItem] = useState('')
    const [itemTitles, setItemTitles] = useState<Record<string, string>>({})
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
    const newItemInputRef = useRef<HTMLInputElement>(null)
    const { mutate: createRoutine } = useCreateRoutine()
    const { mutate: modifyRoutine } = useModifyRoutine()
    const { mutate: duplicateRoutine } = useDuplicateRoutine()
    const { mutate: createItem } = useCreateRoutineItem()
    const { mutate: modifyItem } = useModifyRoutineItem()

    const addDraftItem = () => {
        const title = newItem.trim()
        if (!title) {
            newItemInputRef.current?.focus()
            return
        }
        setNewItem('')
        if (isCreating) setDraftItems((items) => [...items, title])
        else if (routine) createItem({ id: routine.id, title, optimisticId: uuidv4() }, routine.optimisticId)
        newItemInputRef.current?.focus()
    }

    const onNewItemKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        event.stopPropagation()
        if (event.key !== 'Enter' || event.nativeEvent.isComposing || event.keyCode === 229) return
        event.preventDefault()
        if (event.repeat) return
        addDraftItem()
    }

    const create = () => {
        const cleanName = name.trim()
        if (!cleanName) return
        const draft: RoutineDraft = {
            name: cleanName,
            items: [...draftItems, newItem]
                .map((title) => title.trim())
                .filter(Boolean)
                .map((title) => ({ title })),
        }
        createRoutine({ ...draft, optimisticId: uuidv4() })
        close()
    }

    const saveName = () => {
        const cleanName = name.trim()
        if (!routine) return
        if (!cleanName) {
            setName(routine.name)
            return
        }
        if (cleanName !== routine.name) modifyRoutine({ id: routine.id, name: cleanName }, routine.optimisticId)
    }

    const existingItems = routine?.items ?? []
    if (routine && isDeleteConfirmOpen) {
        return (
            <GTModal
                isOpen
                title="Delete routine?"
                type="small"
                canClose
                onClose={() => setIsDeleteConfirmOpen(false)}
                leftButtons={
                    <GTButton styleType="secondary" value="Cancel" onClick={() => setIsDeleteConfirmOpen(false)} />
                }
                rightButtons={
                    <GTButton
                        styleType="destructive"
                        value="Delete routine"
                        disabled={isReadOnly}
                        onClick={() => {
                            modifyRoutine({ id: routine.id, is_deleted: true }, routine.optimisticId)
                            setIsDeleteConfirmOpen(false)
                            close()
                        }}
                    />
                }
            >
                <BodySmall>This routine and its steps will be deleted.</BodySmall>
            </GTModal>
        )
    }
    return (
        <GTModal
            isOpen={isCreating || Boolean(routine)}
            title={isCreating ? 'New routine' : 'Edit routine'}
            type="medium"
            canClose
            onClose={close}
            leftButtons={
                routine ? (
                    <>
                        <GTButton
                            styleType="destructive"
                            value="Delete"
                            disabled={isReadOnly}
                            onClick={() => setIsDeleteConfirmOpen(true)}
                        />
                        <GTButton
                            styleType="secondary"
                            value="Duplicate"
                            icon={icons.clone}
                            disabled={isReadOnly}
                            onClick={() => {
                                duplicateRoutine({ id: routine.id, optimisticId: uuidv4() }, routine.optimisticId)
                                close()
                            }}
                        />
                    </>
                ) : undefined
            }
            rightButtons={
                isCreating ? (
                    <GTButton
                        styleType="primary"
                        value="Create routine"
                        disabled={isReadOnly || !name.trim()}
                        onClick={create}
                    />
                ) : (
                    <GTButton styleType="primary" value="Done" onClick={close} />
                )
            }
        >
            <Section>
                <Label>Name</Label>
                <GTInput
                    value={name}
                    onChange={setName}
                    placeholder="Morning routine"
                    onBlur={saveName}
                    disabled={isReadOnly}
                />
            </Section>
            <Section>
                <Label>Steps</Label>
                {isCreating
                    ? draftItems.map((title, index) => (
                          <ItemRow key={index}>
                              <GTInput
                                  value={title}
                                  onChange={(value) =>
                                      setDraftItems((items) =>
                                          items.map((item, itemIndex) => (itemIndex === index ? value : item))
                                      )
                                  }
                                  disabled={isReadOnly}
                              />
                              <GTButton
                                  styleType="icon"
                                  icon={icons.arrow_up}
                                  tooltipText="Move up"
                                  disabled={isReadOnly || index === 0}
                                  onClick={() =>
                                      setDraftItems((items) => {
                                          const next = [...items]
                                          ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
                                          return next
                                      })
                                  }
                              />
                              <GTButton
                                  styleType="icon"
                                  icon={icons.arrow_down}
                                  tooltipText="Move down"
                                  disabled={isReadOnly || index === draftItems.length - 1}
                                  onClick={() =>
                                      setDraftItems((items) => {
                                          const next = [...items]
                                          ;[next[index], next[index + 1]] = [next[index + 1], next[index]]
                                          return next
                                      })
                                  }
                              />
                              <GTButton
                                  styleType="icon"
                                  icon={icons.trash}
                                  tooltipText="Delete step"
                                  disabled={isReadOnly}
                                  onClick={() =>
                                      setDraftItems((items) => items.filter((_, itemIndex) => itemIndex !== index))
                                  }
                              />
                          </ItemRow>
                      ))
                    : existingItems.map((item, index) => (
                          <ItemRow key={item.id}>
                              <GTInput
                                  value={itemTitles[item.id] ?? item.title}
                                  onChange={(value) => setItemTitles((titles) => ({ ...titles, [item.id]: value }))}
                                  onBlur={() => {
                                      const title = itemTitles[item.id]?.trim()
                                      if (!title) {
                                          setItemTitles((titles) => ({ ...titles, [item.id]: item.title }))
                                      } else if (title !== item.title)
                                          modifyItem({ id: item.id, title }, item.optimisticId)
                                  }}
                                  disabled={isReadOnly || areExistingItemsPending}
                              />
                              <GTButton
                                  styleType="icon"
                                  icon={icons.arrow_up}
                                  tooltipText="Move up"
                                  disabled={isReadOnly || areExistingItemsPending || index === 0}
                                  onClick={() => modifyItem({ id: item.id, id_ordering: index }, item.optimisticId)}
                              />
                              <GTButton
                                  styleType="icon"
                                  icon={icons.arrow_down}
                                  tooltipText="Move down"
                                  disabled={isReadOnly || areExistingItemsPending || index === existingItems.length - 1}
                                  onClick={() => modifyItem({ id: item.id, id_ordering: index + 2 }, item.optimisticId)}
                              />
                              <GTButton
                                  styleType="icon"
                                  icon={icons.trash}
                                  tooltipText="Delete step"
                                  disabled={isReadOnly || areExistingItemsPending}
                                  onClick={() => modifyItem({ id: item.id, is_deleted: true }, item.optimisticId)}
                              />
                          </ItemRow>
                      ))}
                <AddRow>
                    <GTInput
                        ref={newItemInputRef}
                        value={newItem}
                        onChange={setNewItem}
                        placeholder="Add a step"
                        onKeyDown={onNewItemKeyDown}
                        disabled={isReadOnly}
                    />
                    <GTButton
                        styleType="secondary"
                        value="Add"
                        icon={icons.plus}
                        disabled={isReadOnly || !newItem.trim()}
                        onClick={addDraftItem}
                    />
                </AddRow>
            </Section>
        </GTModal>
    )
}

export default RoutineEditModal
